import API_URL from "../config/api";
import { assertNotImpersonatingForSupportAction } from "../utils/impersonation";

import {
  cancelCustomerSubscriptionLunaKey as cancelCustomerSubscriptionLunaKeyRequest,
  cancelSubscriptionLunaKey as cancelSubscriptionLunaKeyRequest,
  createBillingPortalSession as createBillingPortalSessionRequest,
  getCustomerSubscriptionLunaKeys as getCustomerSubscriptionLunaKeysRequest,
  getSubscriptionLunaKeys as getSubscriptionLunaKeysRequest,
} from "./billingApi";
import {
  createCustomer as createCustomerRequest,
  createCustomerNote as createCustomerNoteRequest,
  deleteCustomerNote as deleteCustomerNoteRequest,
  getCustomer as getCustomerRequest,
  getCustomerNotes as getCustomerNotesRequest,
  getCustomers as getCustomersRequest,
  setCustomerSuspension as setCustomerSuspensionRequest,
  updateCustomer as updateCustomerRequest,
  updateCustomerNote as updateCustomerNoteRequest,
  updateCustomerResellerStatus as updateCustomerResellerStatusRequest,
} from "./customersApi";
import httpClient from "./httpClient";

const toQueryString = (params = {}) =>
  new URLSearchParams(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null
    )
  ).toString();

const AMAZON_APPSTORE_PROVIDER_LABEL = "Amazon Appstore";
const STRIPE_PROVIDER_LABEL = "Stripe";

const inferPaymentProvider = (transaction = {}, metadata = {}) => {
  const provider = String(metadata.provider || metadata.platform || "")
    .toLowerCase()
    .trim();
  const stripeCustomerId = String(transaction.stripe_customer_id || "");
  const stripeSubscriptionId = String(
    transaction.stripe_subscription_id || metadata.subscriptionId || ""
  );

  if (
    provider.includes("amazon") ||
    stripeCustomerId.startsWith("amazon:") ||
    stripeSubscriptionId.startsWith("amazon:")
  ) {
    return "amazon";
  }

  if (provider.includes("apple")) {
    return "apple";
  }

  return "stripe";
};

const getPaymentProviderDetails = (transaction = {}, metadata = {}) => {
  const paymentProvider = inferPaymentProvider(transaction, metadata);
  const paymentMethodBrand = metadata.paymentMethodBrand || null;
  const paymentMethodLast4 = metadata.paymentMethodLast4 || null;

  if (paymentProvider === "amazon") {
    return {
      paymentProvider,
      paymentProviderLabel: AMAZON_APPSTORE_PROVIDER_LABEL,
      paymentMethodLabel: "Amazon Appstore Billing",
      paymentMethodBrand: null,
      paymentMethodLast4: null,
      paymentProviderIcon: "fab fa-amazon",
    };
  }

  if (paymentProvider === "apple") {
    return {
      paymentProvider,
      paymentProviderLabel: "Apple",
      paymentMethodLabel: "Apple In-App Purchase",
      paymentMethodBrand,
      paymentMethodLast4,
      paymentProviderIcon: "fab fa-apple",
    };
  }

  return {
    paymentProvider,
    paymentProviderLabel: STRIPE_PROVIDER_LABEL,
    paymentMethodLabel: paymentMethodBrand
      ? `${paymentMethodBrand} ****${paymentMethodLast4 || ""}`.trim()
      : "Credit Card",
    paymentMethodBrand,
    paymentMethodLast4,
    paymentProviderIcon: "fab fa-stripe",
  };
};

const parseTransactionAmount = (value) => {
  const parsed = Number.parseFloat(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

const parseInvoiceTrialEndMs = (metadata = {}) => {
  const value =
    metadata.trialEnd ??
    metadata.trialEndDate ??
    (metadata.isTrial ? metadata.expiresDate : null);

  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number") {
    return value < 1000000000000 ? value * 1000 : value;
  }

  const text = String(value).trim();

  if (!text) return null;

  if (/^\d+$/.test(text)) {
    const numeric = Number.parseInt(text, 10);

    return numeric < 1000000000000 ? numeric * 1000 : numeric;
  }

  const parsedDate = Date.parse(text);

  return Number.isNaN(parsedDate) ? null : parsedDate;
};

const isTruthyFlag = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const getTrialEndValue = (metadata = {}) =>
  metadata.trialEnd ??
  metadata.trialEndDate ??
  (metadata.isTrial ? metadata.expiresDate : null);

const getFallbackTrialStatus = (
  transaction = {},
  metadata = {},
  subscriptionMap = {}
) => {
  if (metadata.createdVia === "db-records-api") return null;

  const trialEndMs = parseInvoiceTrialEndMs(metadata);
  const amount = parseTransactionAmount(transaction.amount);

  if (
    trialEndMs === null ||
    (amount !== 0 && !isTruthyFlag(metadata.isTrial))
  ) {
    return null;
  }

  const subId = transaction.stripe_subscription_id || metadata.subscriptionId;

  if (subId && subscriptionMap[subId]) {
    const hasPaidRenewal = subscriptionMap[subId].some(
      (item) => parseTransactionAmount(item.amount) > 0
    );

    if (hasPaidRenewal) return "converted";
  }

  return trialEndMs >= Date.now() ? "active" : "expired";
};

const getFallbackInvoiceStatus = (metadata = {}, trialStatus = null) => {
  if (trialStatus === "active") return "trial";
  if (trialStatus === "converted") return "converted_trial";
  if (trialStatus === "expired") return "expired_trial";
  if (metadata.refundedAt || metadata.refundChargeId || metadata.refundAmount) {
    return "refunded";
  }

  return "paid";
};

class ApiService {
  constructor() {
    this.baseURL = API_URL;
  }

  async request(endpoint, options = {}) {
    return httpClient.request(endpoint, options);
  }

  async getTickets(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.request(
      `/v1/dashboard/tickets${queryString ? `?${queryString}` : ""}`
    );
  }

  async getTicket(id) {
    return this.request(`/v1/dashboard/tickets/${id}`);
  }

  async getTicketCount() {
    return this.request("/v1/dashboard/tickets/count");
  }

  async createTicket(data) {
    assertNotImpersonatingForSupportAction();

    return this.request("/v1/dashboard/tickets", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateTicket(id, data) {
    assertNotImpersonatingForSupportAction();

    return this.request(`/v1/dashboard/tickets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteTicket(id) {
    assertNotImpersonatingForSupportAction();

    return this.request(`/v1/dashboard/tickets/${id}`, {
      method: "DELETE",
    });
  }

  async addTicketComment(ticketId, data) {
    assertNotImpersonatingForSupportAction();

    return this.request(`/v1/dashboard/tickets/${ticketId}/comments`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateTicketComment(ticketId, commentId, data) {
    assertNotImpersonatingForSupportAction();

    return this.request(
      `/v1/dashboard/tickets/${ticketId}/comments/${commentId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  }

  async sendTicketEmail(data) {
    assertNotImpersonatingForSupportAction();

    return this.request("/v1/dashboard/tickets/email", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async heartbeatTicketPresence(ticketId, isTyping) {
    return this.request(`/v1/dashboard/tickets/${ticketId}/presence`, {
      method: "POST",
      body: JSON.stringify({ isTyping: !!isTyping }),
    });
  }

  async leaveTicketPresence(ticketId) {
    return this.request(`/v1/dashboard/tickets/${ticketId}/presence`, {
      method: "DELETE",
    });
  }

  async deleteAvatar() {
    return this.request("/v1/dashboard/uploads/avatar", {
      method: "DELETE",
    });
  }

  async uploadFile(file) {
    const token = localStorage.getItem("authToken");
    const formData = new FormData();

    formData.append("file", file);

    const response = await fetch(
      `${this.baseURL}/v1/dashboard/uploads/upload`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("authToken");
        window.location.href = "/login";
      }
      throw new Error(`Upload Error: ${response.statusText}`);
    }

    return response.json();
  }

  async uploadAvatar(file) {
    const token = localStorage.getItem("authToken");
    const formData = new FormData();

    formData.append("file", file);

    const response = await fetch(
      `${this.baseURL}/v1/dashboard/uploads/avatar`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("authToken");
        window.location.href = "/login";
      }
      throw new Error(`Upload Error: ${response.statusText}`);
    }

    return response.json();
  }

  async uploadDashboardSportsLeagueOverrideImage(canonicalId, file) {
    const token = localStorage.getItem("authToken");
    const formData = new FormData();

    formData.append("file", file);

    const response = await fetch(
      `${this.baseURL}/v1/dashboard/sports/leagues/${encodeURIComponent(
        canonicalId
      )}/override/image`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("authToken");
        window.location.href = "/login";
      }

      const errorData = await response.json().catch(() => ({
        error: `Upload Error: ${response.statusText}`,
      }));

      const error = new Error(
        errorData.error || `Upload Error: ${response.statusText}`
      );

      error.response = { data: errorData };
      error.details = errorData.details;
      throw error;
    }

    return response.json();
  }

  async addTicketAttachments(ticketId, attachments) {
    assertNotImpersonatingForSupportAction();

    return this.request(`/v1/dashboard/tickets/${ticketId}/attachments`, {
      method: "POST",
      body: JSON.stringify({ attachments }),
    });
  }

  async removeTicketAttachment(ticketId, attachmentId) {
    assertNotImpersonatingForSupportAction();

    return this.request(
      `/v1/dashboard/tickets/${ticketId}/attachments/${attachmentId}`,
      {
        method: "DELETE",
      }
    );
  }

  async getNotifications(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.request(
      `/v1/dashboard/notifications${queryString ? `?${queryString}` : ""}`
    );
  }

  async getNotificationCount() {
    return this.request("/v1/dashboard/notifications/count");
  }

  async markNotificationRead(id) {
    return this.request(`/v1/dashboard/notifications/${id}/read`, {
      method: "PATCH",
    });
  }

  async markAllNotificationsRead() {
    return this.request("/v1/dashboard/notifications/read-all", {
      method: "PATCH",
    });
  }

  async deleteNotification(id) {
    return this.request(`/v1/dashboard/notifications/${id}`, {
      method: "DELETE",
    });
  }

  async clearNotifications() {
    return this.request("/v1/dashboard/notifications", {
      method: "DELETE",
    });
  }

  async getCustomers(params = {}) {
    return getCustomersRequest(params, this.request.bind(this));
  }

  async getCustomer(customerId) {
    return getCustomerRequest(customerId, this.request.bind(this));
  }

  async updateCustomer(customerId, data) {
    return updateCustomerRequest(customerId, data, this.request.bind(this));
  }

  async getCustomerNotes(customerId) {
    return getCustomerNotesRequest(customerId, this.request.bind(this));
  }

  async createCustomerNote(customerId, data) {
    return createCustomerNoteRequest(customerId, data, this.request.bind(this));
  }

  async updateCustomerNote(customerId, noteId, data) {
    return updateCustomerNoteRequest(
      customerId,
      noteId,
      data,
      this.request.bind(this)
    );
  }

  async deleteCustomerNote(customerId, noteId) {
    return deleteCustomerNoteRequest(
      customerId,
      noteId,
      this.request.bind(this)
    );
  }

  async getResellers(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.request(
      `/v1/dashboard/resellers${queryString ? `?${queryString}` : ""}`
    );
  }

  async getReseller(resellerId) {
    return this.request(`/v1/dashboard/resellers/${resellerId}`);
  }

  async updateReseller(resellerId, data) {
    return this.request(`/v1/dashboard/resellers/${resellerId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getResellerTiers() {
    return this.request("/v1/dashboard/resellers/tiers");
  }

  async updateResellerTier(tierId, data) {
    return this.request(`/v1/dashboard/resellers/tiers/${tierId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getResellerLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.request(
      `/v1/dashboard/resellers/logs${queryString ? `?${queryString}` : ""}`
    );
  }

  async getMyReseller() {
    return this.request("/v1/dashboard/resellers/me");
  }

  async getResellerProviderDomains() {
    return this.request("/v1/dashboard/reseller-settings/provider-domains");
  }

  async createResellerProviderDomain(data) {
    return this.request("/v1/dashboard/reseller-settings/provider-domains", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateResellerProviderDomain(providerDomainId, data) {
    return this.request(
      `/v1/dashboard/reseller-settings/provider-domains/${encodeURIComponent(providerDomainId)}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  }

  async deleteResellerProviderDomain(providerDomainId) {
    return this.request(
      `/v1/dashboard/reseller-settings/provider-domains/${encodeURIComponent(providerDomainId)}`,
      { method: "DELETE" }
    );
  }

  async getModuleAuditLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.request(
      `/v1/dashboard/module-logs${queryString ? `?${queryString}` : ""}`
    );
  }

  async setCustomerSuspension(customerId, suspend) {
    return setCustomerSuspensionRequest(
      customerId,
      suspend,
      this.request.bind(this)
    );
  }

  async updateCustomerResellerStatus(customerId, data) {
    return updateCustomerResellerStatusRequest(
      customerId,
      data,
      this.request.bind(this)
    );
  }

  async searchCustomers(query) {
    return this.request(
      `/v1/dashboard/tickets/users/search?q=${encodeURIComponent(query)}`
    );
  }

  // Users Management (Admin only)
  async getUsers(params = {}) {
    const queryParams = toQueryString(params);

    return this.request(
      `/v1/dashboard/users${queryParams ? `?${queryParams}` : ""}`
    );
  }

  async getUserAgentVersions(params = {}) {
    const queryParams = toQueryString(params);

    return this.request(
      `/v1/dashboard/users/user-agents${queryParams ? `?${queryParams}` : ""}`
    );
  }

  async getDeviceDistribution(params = {}) {
    const queryParams = toQueryString(params);

    return this.request(
      `/v1/dashboard/users/device-distribution${queryParams ? `?${queryParams}` : ""}`
    );
  }

  async getUserDomains() {
    return this.request(`/v1/dashboard/users/domains`);
  }

  async getUser(userId) {
    return this.request(
      `/v1/dashboard/users/${encodeURIComponent(String(userId))}`
    );
  }

  async getUserAssociations(userId, collection, params = {}) {
    const queryParams = toQueryString(params);

    return this.request(
      `/v1/dashboard/users/${encodeURIComponent(String(userId))}/associations/${encodeURIComponent(String(collection))}${queryParams ? `?${queryParams}` : ""}`
    );
  }

  // Reseller-managed dashboard access for one playlist (Admin only)
  async getPlaylistManagedLinks(userId) {
    return this.request(
      `/v1/dashboard/users/${encodeURIComponent(String(userId))}/managed-links`
    );
  }

  async findPlaylistManagedLinkCandidate(userId, query) {
    const queryParams = toQueryString({ query });

    return this.request(
      `/v1/dashboard/users/${encodeURIComponent(String(userId))}/managed-links/candidates?${queryParams}`
    );
  }

  async createPlaylistManagedLink(userId, { registrationId, note } = {}) {
    const body = { registrationId };

    if (note) body.note = note;

    return this.request(
      `/v1/dashboard/users/${encodeURIComponent(String(userId))}/managed-links`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );
  }

  async revokePlaylistManagedLink(userId, linkId) {
    return this.request(
      `/v1/dashboard/users/${encodeURIComponent(String(userId))}/managed-links/${encodeURIComponent(String(linkId))}`,
      { method: "DELETE" }
    );
  }

  async getAdmins() {
    return this.request(`/v1/dashboard/admins`);
  }

  async updateUserLoginStatus(status) {
    // Update user login/logout status
    try {
      return this.request(`/v1/dashboard/users/status`, {
        method: "POST",
        body: JSON.stringify({ status, timestamp: new Date().toISOString() }),
      });
    } catch (error) {
      // Silently fail if endpoint doesn't exist - not critical for functionality
      return Promise.resolve({ success: true });
    }
  }

  async createBillingPortalSession(customerId) {
    return createBillingPortalSessionRequest(
      customerId,
      this.request.bind(this)
    );
  }

  async getSubscriptionLunaKeys(params = {}) {
    return getSubscriptionLunaKeysRequest(params, this.request.bind(this));
  }

  async cancelSubscriptionLunaKey(redeemCodeId, options = {}) {
    return cancelSubscriptionLunaKeyRequest(
      redeemCodeId,
      options,
      this.request.bind(this)
    );
  }

  async getCustomerSubscriptionLunaKeys(customerId, params = {}) {
    return getCustomerSubscriptionLunaKeysRequest(
      customerId,
      params,
      this.request.bind(this)
    );
  }

  async cancelCustomerSubscriptionLunaKey(
    customerId,
    redeemCodeId,
    options = {}
  ) {
    return cancelCustomerSubscriptionLunaKeyRequest(
      customerId,
      redeemCodeId,
      options,
      this.request.bind(this)
    );
  }

  async globalSearch(query) {
    try {
      // Use the new search endpoint
      const response = await this.request(
        `/v1/dashboard/search?q=${encodeURIComponent(query)}`
      );

      if (response.status === 200 && response.data) {
        // Transform the response to match the expected format
        const { data } = response;

        return {
          registrations: data.registrations || [],
          transactions: data.transactions || [],
          faqs: data.faqs || [],
          tickets: data.tickets || [],
          ticketComments: data.ticketComments || [],
          users: data.users || [],
          admins: data.admins || [],
          redeemCodes: data.redeemCodes || [],
          // Legacy fields for backward compatibility
          customers: data.registrations || [],
          invoices:
            data.transactions?.map((t) => ({
              invoiceNumber:
                t.metadata?.prettyInvoiceId ||
                `TXN-${t.transaction_id?.substring(0, 8)}`,
              customer: t.metadata?.customerDetails?.name || "Customer",
              amount: t.amount,
              status: "paid",
              date: t.created_at,
            })) || [],
          billings: [],
          lunovaKeys: [],
        };
      }

      throw new Error("Invalid response from search endpoint");
    } catch (error) {
      console.error("Search API error:", error);

      // Return empty results on error
      return {
        registrations: [],
        transactions: [],
        faqs: [],
        tickets: [],
        ticketComments: [],
        users: [],
        admins: [],
        redeemCodes: [],
        customers: [],
        invoices: [],
        billings: [],
        lunovaKeys: [],
      };
    }
  }

  async createCustomer(customerData) {
    return createCustomerRequest(customerData, this.request.bind(this));
  }

  async login(email, password) {
    try {
      const response = await fetch(`${this.baseURL}/v1/dashboard/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        return data;
      }

      throw new Error(data.error || data.message || "Login failed");
    } catch (error) {
      throw error;
    }
  }

  async register(registrationData) {
    try {
      const response = await fetch(
        `${this.baseURL}/v1/dashboard/registration`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(registrationData),
        }
      );

      const data = await response.json();

      if (response.ok) {
        return data;
      }

      throw new Error(data.error || data.message || "Registration failed");
    } catch (error) {
      throw error;
    }
  }

  async validateToken(token) {
    try {
      const response = await fetch(
        `${this.baseURL}/v1/dashboard/validate-token`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        return data;
      }

      throw new Error("Invalid or expired token");
    } catch (error) {
      throw error;
    }
  }

  async sendHeartbeat(token) {
    try {
      const response = await fetch(`${this.baseURL}/v1/dashboard/heartbeat`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        return data;
      }

      throw new Error("Heartbeat failed");
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(token, password) {
    try {
      const response = await fetch(
        `${this.baseURL}/v1/dashboard/registration/reset-password`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ password }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        return data;
      }

      throw new Error(data.message || "Failed to reset password");
    } catch (error) {
      throw error;
    }
  }

  async forgotPassword(email) {
    try {
      const response = await fetch(
        `${this.baseURL}/v1/dashboard/registration/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        return data;
      }

      throw new Error(data.message || "Failed to send reset email");
    } catch (error) {
      throw error;
    }
  }

  async getPlaylists() {
    const response = await this.request("/v1/dashboard/accounts/playlists");
    const payload = response?.data;

    if (!payload || Array.isArray(payload)) {
      return response;
    }

    const accounts = payload.accounts || [];
    const playlists =
      payload.playlists ||
      accounts.flatMap((account) => account.playlists || []);

    return {
      ...response,
      accounts,
      data: playlists,
      maxPlaylistsPerAccount: payload.maxPlaylistsPerAccount,
    };
  }

  async getPlaylistAccounts(params = {}) {
    const queryString = toQueryString(params);

    return this.request(
      `/v1/dashboard/accounts/playlists${queryString ? `?${queryString}` : ""}`
    );
  }

  async createPlaylistAccount(data) {
    return this.request("/v1/dashboard/accounts", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async addPlaylistToAccount(accountId, data) {
    return this.request(
      `/v1/dashboard/accounts/${encodeURIComponent(accountId)}/playlists`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  }

  async updatePlaylist(playlistId, data) {
    if (data?.accountId) {
      const { accountId, ...payload } = data;

      return this.request(
        `/v1/dashboard/accounts/${encodeURIComponent(
          accountId
        )}/playlists/${encodeURIComponent(playlistId)}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        }
      );
    }

    return this.request(`/v1/dashboard/playlists/${playlistId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deletePlaylist(playlistId, data) {
    if (data?.accountId) {
      return this.request(
        `/v1/dashboard/accounts/${encodeURIComponent(
          data.accountId
        )}/playlists/${encodeURIComponent(playlistId)}`,
        {
          method: "DELETE",
          body: JSON.stringify({
            code: data.code,
            removeCustomer: data.removeCustomer === true,
          }),
        }
      );
    }

    return this.request(`/v1/dashboard/playlists/${playlistId}`, {
      method: "DELETE",
      body: JSON.stringify(data),
    });
  }

  async updatePlaylistAutoRenew(playlistId, autoRenew, accountId = null) {
    if (accountId) {
      return this.request(
        `/v1/dashboard/accounts/${encodeURIComponent(accountId)}/auto-renew`,
        {
          method: "PATCH",
          body: JSON.stringify({ autoRenew }),
        }
      );
    }

    return this.request(`/v1/dashboard/playlists/${playlistId}/auto-renew`, {
      method: "PATCH",
      body: JSON.stringify({ autoRenew }),
    });
  }

  async forceLogoutPlaylistSessions(playlistId, accountId = null) {
    if (accountId) {
      return this.request(
        `/v1/dashboard/accounts/${encodeURIComponent(accountId)}/force-logout`,
        {
          method: "POST",
        }
      );
    }

    return this.request(`/v1/dashboard/playlists/${playlistId}/force-logout`, {
      method: "POST",
    });
  }

  async getPlaylistDevices(playlistId, accountId = null) {
    if (accountId) {
      return this.request(
        `/v1/dashboard/accounts/${encodeURIComponent(accountId)}/devices`
      );
    }

    return this.request(`/v1/dashboard/playlists/${playlistId}/devices`);
  }

  async getCustomerPlaylists(customerId) {
    return this.request(`/v1/dashboard/customer/playlists/${customerId}`);
  }

  async getCustomerPlaylistDevices(customerId, playlistId) {
    return this.request(
      `/v1/dashboard/customers/${encodeURIComponent(customerId)}/playlists/${encodeURIComponent(playlistId)}/devices`
    );
  }

  // LunaKey login method
  async loginWithLunaKey(lunaKey) {
    return this.request("/v1/login/lunakey", {
      method: "POST",
      body: JSON.stringify({ code: lunaKey }),
    });
  }

  // Direct credential login method for web player
  async loginWithCredentials(url, username, password) {
    // Ensure URL has protocol
    const uriHost = url.startsWith("http") ? url : `https://${url}`;

    return this.request("/v1/login", {
      method: "POST",
      body: JSON.stringify({
        uriHost,
        username,
        password,
      }),
    });
  }

  async getAvatarsDirect() {
    return this.request("/v1/avatars");
  }

  async getPlayerPlaylists() {
    return this.request("/v1/dashboard/player/playlists");
  }

  async getFavorites() {
    return this.request("/v1/dashboard/playlist/favorites");
  }

  async addFavorite(data) {
    return this.request("/v1/dashboard/playlist/favorites", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async removeFavorite(favoriteId) {
    if (favoriteId) {
      return this.request(`/v1/dashboard/playlist/favorites/${favoriteId}`, {
        method: "DELETE",
      });
    } else {
      return this.request(`/v1/dashboard/playlist/favorites`, {
        method: "DELETE",
      });
    }
  }

  async updatePlaylistSettings(playlistId, settings) {
    return this.request(
      `/v1/dashboard/customer/playlists/${playlistId}/settings`,
      {
        method: "PUT",
        body: JSON.stringify(settings),
      }
    );
  }

  async getUserSettingsByCode(code) {
    return this.request(`/v1/user/settings?code=${encodeURIComponent(code)}`);
  }

  async getPlayerStats() {
    return this.request("/v1/dashboard/player/stats");
  }

  async getPlayerUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.request(
      `/v1/dashboard/player/users${queryString ? `?${queryString}` : ""}`
    );
  }

  async getUnclaimedRedeemCodes() {
    return this.request("/v1/dashboard/redeem-codes/unclaimed");
  }

  async getUnclaimedRedeemCodeTypes() {
    return this.request("/v1/dashboard/redeem-codes/unclaimed/types");
  }

  async redeemCode(data) {
    return this.request("/v1/dashboard/redeem-codes/redeem", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getDashboardProfile() {
    return this.request("/v1/dashboard/profile");
  }

  async getDashboardStats() {
    return this.request("/v1/dashboard/stats");
  }

  async getStaffOnline() {
    return this.request("/v1/dashboard/staff-online");
  }

  async getClientActivity() {
    return this.request("/v1/dashboard/client-activity");
  }

  async getClientActivityGeo() {
    return this.request("/v1/dashboard/client-activity/geo");
  }

  async getResellerPlaylistGeo() {
    return this.request("/v1/dashboard/accounts/playlists/geo");
  }

  async impersonateCustomer(registrationId) {
    return this.request(`/v1/dashboard/login/impersonate`, {
      method: "POST",
      body: JSON.stringify({ registrationId }),
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request("/v1/dashboard/registration/password", {
      method: "PATCH",
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });
  }

  async getChartData(timeRange = "today") {
    try {
      return this.request(
        `/v1/dashboard/transactions/stats?timeRange=${timeRange}`
      );
    } catch (err) {
      console.error("Failed to get chart data:", err);
      throw err;
    }
  }

  async getInvoices(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await this.request(
        `/v1/dashboard/transactions${queryString ? `?${queryString}` : ""}`
      );

      // Build subscription map for trial detection
      const subscriptionMap = {};

      (response.data || []).forEach((t) => {
        const subId = t.stripe_subscription_id;

        if (subId) {
          if (!subscriptionMap[subId]) {
            subscriptionMap[subId] = [];
          }
          subscriptionMap[subId].push(t);
        }
      });

      const invoices = (response.data || []).map((transaction) => {
        const createdAt = new Date(transaction.created_at);
        const metadata = transaction.metadata || {};
        const paymentProviderDetails = getPaymentProviderDetails(
          transaction,
          metadata
        );

        // Support both Stripe (prettyInvoiceId) and Apple (prettyTransactionId) transaction formats
        // Fallback to transaction_id if no pretty ID is available
        const invoiceNumber =
          metadata.prettyTransactionId ||
          metadata.prettyInvoiceId ||
          metadata.invoiceId ||
          `TXN-${transaction.transaction_id?.substring(0, 8)?.toUpperCase()}`;
        const customerDetails = metadata.customerDetails || {};
        const customerName = customerDetails.name || "Lunova Customer";
        const customerEmail = customerDetails.email || "";
        const customerAddress = customerDetails.address || {};

        // Use actual subscription_type from transaction, not metadata.subscriptionInterval
        // metadata.subscriptionInterval can be "trial" which would incorrectly show as "Annual"
        const interval =
          transaction.subscription_type ||
          metadata.subscriptionInterval ||
          "monthly";
        const subscriptionType =
          interval === "monthly" || interval === "month"
            ? "Monthly Subscription"
            : interval === "quarterly" || interval === "quarter"
              ? "Quarterly Subscription"
              : interval === "yearly" || interval === "year"
                ? "Annual Subscription"
                : "Subscription";

        // Determine invoice status from API classification when available,
        // falling back to the saved transaction metadata for older responses.
        const amount = parseTransactionAmount(transaction.amount);
        const trialStatus =
          transaction.trialStatus ||
          getFallbackTrialStatus(transaction, metadata, subscriptionMap);
        const status =
          transaction.invoiceStatus ||
          getFallbackInvoiceStatus(metadata, trialStatus);
        const isTrialTransaction =
          transaction.isTrialTransaction ?? trialStatus === "active";

        // Build line items from metadata or create default
        let items = [];

        if (metadata.lineItems && metadata.lineItems.length > 0) {
          // Transform line items from metadata to include rate and amount
          const itemAmount = amount / metadata.lineItems.length;

          items = metadata.lineItems.map((item) => ({
            description: item.description || "Lunova Player Subscription",
            quantity: item.quantity || 1,
            rate: itemAmount / (item.quantity || 1),
            amount: itemAmount,
          }));
        } else {
          let description;

          if (isTrialTransaction && metadata.trialEnd) {
            // Calculate trial duration in days
            const trialEndDate = new Date(metadata.trialEnd * 1000);
            const durationDays = Math.ceil(
              (trialEndDate - createdAt) / (1000 * 60 * 60 * 24)
            );

            description = `Lunova Player - ${durationDays} Day Subscription (Trial)`;
          } else if (isTrialTransaction) {
            description = `Lunova Player - ${subscriptionType} (Trial)`;
          } else {
            description = `Lunova Player - ${subscriptionType}`;
          }

          items = [
            {
              description,
              quantity: metadata.totalCodes || 1,
              rate: amount,
              amount,
            },
          ];
        }

        return {
          transactionId: transaction.transaction_id,
          id: invoiceNumber,
          invoiceNumber,
          prettyInvoiceId: metadata.prettyInvoiceId,
          customer: customerName,
          customerEmail,
          customerAddress: [
            customerAddress.postal_code,
            customerAddress.country,
          ]
            .filter(Boolean)
            .join(", "),
          amount,
          total: amount,
          status,
          issueDate: createdAt.toISOString(),
          createdAt: createdAt.toISOString(),
          items,
          stripeCustomerId: transaction.stripe_customer_id,
          stripeSubscriptionId:
            transaction.stripe_subscription_id || metadata.subscriptionId,
          stripeInvoiceId: metadata.invoiceId,
          redeemCodes: transaction.redeem_codes || [],
          billingReason: metadata.billingReason,
          isRenewal: metadata.isRenewal || false,
          isTrialTransaction,
          trialEnd: getTrialEndValue(metadata),
          trialStatus,
          ...paymentProviderDetails,
          subscriptionType: transaction.subscription_type,
          registrationId: transaction.registration_id,
        };
      });

      return {
        facets: response.facets || {},
        invoices,
        pagination: response.pagination,
        summary: response.summary || null,
      };
    } catch (error) {
      throw error;
    }
  }

  async getInvoice(invoiceId) {
    try {
      // Use the new endpoint to fetch a single transaction
      const response = await this.request(
        `/v1/dashboard/transactions/${invoiceId}`
      );
      const transaction = response.data;

      if (!transaction) {
        throw new Error("Invoice not found");
      }

      const createdAt = new Date(transaction.created_at);
      const metadata = transaction.metadata || {};
      const paymentProviderDetails = getPaymentProviderDetails(
        transaction,
        metadata
      );

      const invoiceNumber =
        metadata.prettyInvoiceId ||
        metadata.invoiceId ||
        `LUN-${createdAt.getFullYear()}-${String(1).padStart(5, "0")}`;

      const customerDetails = metadata.customerDetails || {};
      const customerName = customerDetails.name || "Lunova Customer";
      const customerEmail = customerDetails.email || "";
      const customerAddress = customerDetails.address || {};

      // Use actual subscription_type from transaction, not metadata.subscriptionInterval
      // metadata.subscriptionInterval can be "trial" which would incorrectly show as "Annual"
      const interval =
        transaction.subscription_type ||
        metadata.subscriptionInterval ||
        "monthly";
      const subscriptionType =
        interval === "monthly" || interval === "month"
          ? "Monthly Subscription"
          : interval === "quarterly" || interval === "quarter"
            ? "Quarterly Subscription"
            : interval === "yearly" || interval === "year"
              ? "Annual Subscription"
              : "Subscription";

      // Determine invoice status from API classification when available,
      // falling back to the saved transaction metadata for older responses.
      const amount = parseTransactionAmount(transaction.amount);
      const trialStatus =
        transaction.trialStatus ||
        getFallbackTrialStatus(transaction, metadata, {
          [transaction.stripe_subscription_id || metadata.subscriptionId]: [
            transaction,
          ],
        });
      const status =
        transaction.invoiceStatus ||
        getFallbackInvoiceStatus(metadata, trialStatus);
      const isTrialTransaction =
        transaction.isTrialTransaction ?? trialStatus === "active";

      // Build line items from metadata or create default
      let items = [];

      if (metadata.lineItems && metadata.lineItems.length > 0) {
        // Transform line items from metadata to include rate and amount
        const itemAmount = amount / metadata.lineItems.length;

        items = metadata.lineItems.map((item) => ({
          description: item.description || "Lunova Player Subscription",
          quantity: item.quantity || 1,
          rate: itemAmount / (item.quantity || 1),
          amount: itemAmount,
        }));
      } else {
        let description;

        if (isTrialTransaction && metadata.trialEnd) {
          // Calculate trial duration in days
          const trialEndDate = new Date(metadata.trialEnd * 1000);
          const durationDays = Math.ceil(
            (trialEndDate - createdAt) / (1000 * 60 * 60 * 24)
          );

          description = `Lunova Player - ${durationDays} Day Subscription (Trial)`;
        } else if (isTrialTransaction) {
          description = `Lunova Player - ${subscriptionType} (Trial)`;
        } else {
          description = `Lunova Player - ${subscriptionType}`;
        }

        items = [
          {
            description,
            quantity: metadata.totalCodes || 1,
            rate: amount,
            amount,
          },
        ];
      }

      return {
        transactionId: transaction.transaction_id,
        id: invoiceNumber,
        invoiceNumber,
        prettyInvoiceId: metadata.prettyInvoiceId,
        customer: customerName,
        customerEmail,
        customerAddress: [customerAddress.postal_code, customerAddress.country]
          .filter(Boolean)
          .join(", "),
        amount,
        total: amount,
        status,
        issueDate: createdAt.toISOString(),
        createdAt: createdAt.toISOString(),
        items,
        stripeCustomerId: transaction.stripe_customer_id,
        stripeSubscriptionId:
          transaction.stripe_subscription_id || metadata.subscriptionId,
        stripeInvoiceId: metadata.invoiceId,
        redeemCodes: transaction.redeem_codes || [],
        billingReason: metadata.billingReason,
        isRenewal: metadata.isRenewal || false,
        isTrialTransaction,
        trialEnd: getTrialEndValue(metadata),
        trialStatus,
        ...paymentProviderDetails,
        subscriptionType: transaction.subscription_type,
        registrationId: transaction.registration_id,
      };
    } catch (error) {
      throw error;
    }
  }

  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.request(`${endpoint}${queryString ? `?${queryString}` : ""}`);
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: "DELETE",
    });
  }

  async getFAQs(activeOnly = true) {
    return this.request(`/v1/dashboard/faqs?active_only=${activeOnly}`);
  }

  async getFAQ(faqId) {
    return this.request(`/v1/dashboard/faqs/${faqId}`);
  }

  async getDashboardSportsCategories() {
    return this.request("/v1/dashboard/sports/categories");
  }

  async getDashboardSportsLeagues(params = {}) {
    const clean = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      )
    );
    const queryString = new URLSearchParams(clean).toString();

    return this.request(
      `/v1/dashboard/sports/leagues${queryString ? `?${queryString}` : ""}`
    );
  }

  async getDashboardSportsLeagueOverride(canonicalId) {
    return this.request(
      `/v1/dashboard/sports/leagues/${encodeURIComponent(canonicalId)}/override`
    );
  }

  async saveDashboardSportsLeagueOverride(canonicalId, data) {
    return this.request(
      `/v1/dashboard/sports/leagues/${encodeURIComponent(
        canonicalId
      )}/override`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      }
    );
  }

  async disableDashboardSportsLeagueOverride(canonicalId) {
    return this.request(
      `/v1/dashboard/sports/leagues/${encodeURIComponent(
        canonicalId
      )}/override`,
      {
        method: "DELETE",
      }
    );
  }

  async createFAQ(data) {
    return this.request("/v1/dashboard/faqs", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateFAQ(faqId, data) {
    return this.request(`/v1/dashboard/faqs/${faqId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteFAQ(faqId) {
    return this.request(`/v1/dashboard/faqs/${faqId}`, {
      method: "DELETE",
    });
  }

  async bulkCreateFAQs(faqs) {
    return this.request("/v1/dashboard/faqs/bulk", {
      method: "POST",
      body: JSON.stringify({ faqs }),
    });
  }

  async reorderFAQs(updates) {
    return this.request("/v1/dashboard/faqs/reorder", {
      method: "POST",
      body: JSON.stringify({ updates }),
    });
  }

  // Predefined Replies - Categories
  async getPredefinedReplyCategories(activeOnly = false) {
    return this.request(
      `/v1/dashboard/predefined-replies/categories?active_only=${activeOnly}`
    );
  }

  async getPredefinedReplyCategory(categoryId) {
    return this.request(
      `/v1/dashboard/predefined-replies/categories/${categoryId}`
    );
  }

  async createPredefinedReplyCategory(data) {
    return this.request("/v1/dashboard/predefined-replies/categories", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updatePredefinedReplyCategory(categoryId, data) {
    return this.request(
      `/v1/dashboard/predefined-replies/categories/${categoryId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  }

  async deletePredefinedReplyCategory(categoryId) {
    return this.request(
      `/v1/dashboard/predefined-replies/categories/${categoryId}`,
      {
        method: "DELETE",
      }
    );
  }

  async reorderPredefinedReplyCategories(updates) {
    return this.request("/v1/dashboard/predefined-replies/categories/reorder", {
      method: "POST",
      body: JSON.stringify({ updates }),
    });
  }

  // Predefined Replies - Replies
  async getPredefinedReplies(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.request(
      `/v1/dashboard/predefined-replies${queryString ? `?${queryString}` : ""}`
    );
  }

  async getPredefinedRepliesGrouped() {
    return this.request("/v1/dashboard/predefined-replies/grouped");
  }

  async getPredefinedReplyVariables() {
    return this.request("/v1/dashboard/predefined-replies/variables");
  }

  async getPredefinedReply(replyId) {
    return this.request(`/v1/dashboard/predefined-replies/${replyId}`);
  }

  async createPredefinedReply(data) {
    return this.request("/v1/dashboard/predefined-replies", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updatePredefinedReply(replyId, data) {
    return this.request(`/v1/dashboard/predefined-replies/${replyId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deletePredefinedReply(replyId) {
    return this.request(`/v1/dashboard/predefined-replies/${replyId}`, {
      method: "DELETE",
    });
  }

  async usePredefinedReply(replyId) {
    return this.request(`/v1/dashboard/predefined-replies/${replyId}/use`, {
      method: "POST",
    });
  }

  async reorderPredefinedReplies(updates) {
    return this.request("/v1/dashboard/predefined-replies/reorder", {
      method: "POST",
      body: JSON.stringify({ updates }),
    });
  }

  // App Messages - admin/service/reseller publish + admin list/detail/delete.
  // Endpoints live under /v2/app/messages (shared with the client-facing
  // ack endpoint). The dashboard uses its admin JWT; the API's
  // requireAdminOrService middleware authenticates it and routes per role.

  async getAppMessages(params = {}) {
    const clean = Object.fromEntries(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
      )
    );
    const queryString = new URLSearchParams(clean).toString();

    return this.request(
      `/v2/app/messages${queryString ? `?${queryString}` : ""}`
    );
  }

  async getAppMessage(id) {
    return this.request(`/v2/app/messages/${id}`);
  }

  async publishAppMessage(data) {
    return this.request("/v2/app/messages", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async deleteAppMessage(id) {
    return this.request(`/v2/app/messages/${id}`, { method: "DELETE" });
  }

  async searchAppMessageDomains(q = "", limit = 20) {
    const params = new URLSearchParams({ limit: String(limit) });

    if (q) params.set("q", q);

    return this.request(`/v2/app/messages/scope/domains?${params.toString()}`);
  }

  async searchAppMessageLunakeys(q = "", limit = 20) {
    const params = new URLSearchParams({ limit: String(limit) });

    if (q) params.set("q", q);

    return this.request(`/v2/app/messages/scope/lunakeys?${params.toString()}`);
  }
}

const apiService = new ApiService();

export default apiService;
